import { Fragment, useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from '@softland/forms';
import { yupResolver } from '@softland/forms/resolver';
import { otpValidation } from './validation';
import { useLocation, useNavigate } from 'react-router-dom';
import { CredentialsResponseModel, otp, UserType } from 'modules/auth/api/auth.api';

interface OtpFormModel {
  trustDevice: boolean;
  otp: string;
}

export const Otp = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);
  const [otpData, setOtpData] = useState<CredentialsResponseModel | null>(null);

  const { control, handleSubmit, reset } = useForm<OtpFormModel>({
    defaultValues: {
      otp: '',
      trustDevice: false,
    },
    resolver: yupResolver(otpValidation),
  });

  useEffect(() => {
    if (location.state?.twoFaState) {
      setOtpData(location.state.twoFaState);
      setReturnUrl(location.state.returnUrl);
    } else {
      navigate('/error', {
        state: {
          message: 'page not found',
        },
      });
    }
  }, [location.state, navigate, reset]);

  const onSubmit: SubmitHandler<OtpFormModel> = async data => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await otp({
        token: otpData!.token,
        otp: data.otp,
        trustDevice: data.trustDevice,
      });

      if (returnUrl) {
        window.location.replace(returnUrl);
      } else {
        window.location.replace(response.data);
      }
    } catch (error: any) {
      setErrorMessage('Enter a valid code');
    }

    setLoading(false);
  };

  return (
    <Stack
      padding={3}
      gap={3}
      justifyContent="center"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : 'none',
      }}
    >
      {otpData?.token ? (
        <Fragment>
          <Box>
            <Typography variant="h5" className="qa-two-fa-title" color="text.primary">
              Verification
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ color: theme.palette.text.secondary, marginTop: '7px', marginBottom: '15px' }}
              className="qa-welcome-message"
            >
              Enter the code generated by Google Authenticator app on your device.
            </Typography>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Controller
                name="otp"
                control={control}
                render={({ field: { onBlur, value, onChange }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    size="medium"
                    id="qa-verification"
                    label="Verification Code"
                    variant="outlined"
                    onBlur={onBlur}
                    value={value}
                    onChange={e => onChange(e.target.value.trim())}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
              {otpData.userType === UserType.BACKOFFICE && (
                <Controller
                  name="trustDevice"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      control={<Checkbox size="small" checked={value} onChange={onChange} />}
                      label="Trust this device"
                    />
                  )}
                />
              )}
              <Box>
                <LoadingButton
                  id="qa-login-button"
                  loading={loading}
                  size="large"
                  variant="contained"
                  type="submit"
                  fullWidth
                >
                  Confirm
                </LoadingButton>
                {errorMessage && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.error.main, padding: theme.spacing(1) }}
                    className="qa-error-message"
                  >
                    {errorMessage}
                  </Typography>
                )}
              </Box>
            </Stack>
          </form>
        </Fragment>
      ) : (
        <CircularProgress />
      )}
    </Stack>
  );
};
